import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef, useCallback } from 'react'
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { trans } from '../../../../../_providers/Translation'

// images
import popupDefaultHeadImage from "@/assets/img/modals/icons/head.svg"
import markerImg from '../../../../../assets/img/leeflet/marker-icon.png'

import '../../../../../assets/css/modals/employees.css'
import { Maps } from '../../../../../_config/maps'
import loader from '../../../../../assets/img/loader.svg'

let timeout

const CENTER = { lat: 42.70060440808085, lng: 23.318481445312504 }

// Google "address_components" types mapped to readable keys
const ADDRESS_COMPONENTS = {
    street_number: 'street_number',
    route: 'street',
    intersection: 'intersection',
    premise: 'premise',
    subpremise: 'subpremise',
    floor: 'floor',
    room: 'room',
    neighborhood: 'neighborhood',
    sublocality: 'sublocality',
    sublocality_level_1: 'sublocality_level_1',
    sublocality_level_2: 'sublocality_level_2',
    locality: 'city',
    postal_town: 'postal_town',
    administrative_area_level_3: 'municipality',
    administrative_area_level_2: 'district',
    administrative_area_level_1: 'region',
    country: 'country',
    postal_code: 'post_code',
    postal_code_suffix: 'post_code_suffix',
}

const parseAddressComponents = result => {
    const details = {}
    const components = result.address_components || []

    components.forEach(component => {
        component.types.forEach(type => {
            const key = ADDRESS_COMPONENTS[type]
            if (!key || details[key]) return

            details[key] = component.long_name

            if (key === 'country') details.country_code = component.short_name
            if (key === 'region') details.region_code = component.short_name
        })
    })

    return details
}

// Log everything we know about the location of a placed pin
const logLocationDetails = (label, result, results, coords) => {
    const components = parseAddressComponents(result)

    console.group('[Address] ' + label)
    console.log('coordinates:', coords)
    console.log('address:', result.formatted_address)
    console.log('post_code:', components.post_code)
    console.log('country:', components.country, components.country_code)
    console.log('region:', components.region)
    console.log('city:', components.city)
    console.log('neighborhood:', components.neighborhood)
    console.log('place_id:', result.place_id)
    console.log('types:', result.types)
    console.log('location_type:', result.geometry && result.geometry.location_type)
    console.log('plus_code:', result.plus_code)
    console.log('components:', components)
    console.table(components)
    console.log('raw result:', result)
    console.log('all results:', results)
    console.groupEnd()

    return components
}

// ─── Outer wrapper: provides the Google Maps API context ─────────────────────
function Address(props, ref) {
    return (
        <APIProvider apiKey={Maps.google}>
            <AddressContent {...props} forwardedRef={ref} />
        </APIProvider>
    )
}

// ─── Main content component ───────────────────────────────────────────────────
function AddressContent({ forwardedRef, ...props }) {
    const inputRef = useRef(null)

    // Guards against a slow lookup overwriting the details of a newer pin
    const geocodeRequestRef = useRef(0)

    const placesLib = useMapsLibrary('places')

    const [state, setState] = useState({
        overlay: false,
        modal: false,
        loading: false,
        selected: null,
        lat: null,
        lon: null,
        place_id: null,
        postcode: null,
        data: [],
        dataLoaded: false,
        onSave: null,
    })

    const [services, setServices] = useState({
        autocomplete: null,
        geocoder: null,
    })

    useImperativeHandle(forwardedRef, () => ({
        open: () => handleOpen(),
        close: () => handleClose(),
        onSave: fn => setState(prev => ({ ...prev, onSave: fn })),
    }))

    // Initialize Google Places / Geocoder services once the library is ready
    useEffect(() => {
        if (placesLib && window.google) {
            setServices({
                autocomplete: new placesLib.AutocompleteService(),
                geocoder: new window.google.maps.Geocoder(),
            })
        }
    }, [placesLib])

    // Pre-populate from props (selected address + coordinates)
    useEffect(() => {
        handleSelect(props.selected, props.params.lat, props.params.lon)
    }, [props.selected, props.params.lat, props.params.lon])

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleOpen = () => {
        setState(prev => ({ ...prev, overlay: true }))
        setTimeout(() => setState(prev => ({ ...prev, modal: true })), 50)
    }

    const handleClose = e => {
        if (e) e.preventDefault()
        setState(prev => ({ ...prev, modal: false }))
        setTimeout(() => setState(prev => ({ ...prev, overlay: false })), 50)
    }

    const handleSelect = (name, lat, lon) => {
        setState(prev => ({ ...prev, selected: name, lat, lon }))
    }

    const handleSelectLatLon = useCallback((lat, lon, place_id = null) => {
        setState(prev => ({ ...prev, lat, lon, place_id, postcode: null }))
    }, [])

    // Geocodes a placed pin, logs the full location and keeps the resolved post code
    const resolveLocation = useCallback((request, { coords = null, place_id = null, label }) => {
        if (!services.geocoder) {
            console.warn('[Address] ' + label + ' - the geocoder is not ready yet', coords)
            return
        }

        const requestId = ++geocodeRequestRef.current

        services.geocoder.geocode(request)
            .then(({ results }) => {
                // A newer pin was placed while this lookup was in flight
                if (requestId !== geocodeRequestRef.current) return

                if (!results || !results.length) {
                    console.warn('[Address] no geocoding results for', request)
                    return
                }

                const result = results[0]
                const location = result.geometry && result.geometry.location
                const position = coords || (location ? { lat: location.lat(), lon: location.lng() } : null)

                // The search results only carry a place id - the geocoder resolves the coordinates
                if (!coords && position) handleSelectLatLon(position.lat, position.lon, place_id)

                const components = logLocationDetails(label, result, results, position)

                setState(prev => ({ ...prev, postcode: components.post_code || null }))
            })
            .catch(error => console.error('[Address] geocoding failed', error))
    }, [services.geocoder, handleSelectLatLon])

    // Pin dropped directly on the map
    const handleMapClick = useCallback((lat, lon) => {
        handleSelectLatLon(lat, lon)
        resolveLocation({ location: { lat, lng: lon } }, {
            coords: { lat, lon },
            label: 'pin placed on the map',
        })
    }, [handleSelectLatLon, resolveLocation])

    const handleSave = e => {
        e.preventDefault()
        saveSelected(state.selected, state.lat, state.lon, state.postcode)
        handleClose()
    }

    const saveSelected = (selected, lat, lon, postcode) => {
        if (typeof props.onSave === 'function') {
            props.onSave(selected, lat, lon, postcode)
        } else if (typeof state.onSave === 'function') {
            state.onSave(selected, lat, lon, postcode)
        }
    }

    const handleSearch = e => {
        const name = e.target.value

        setState(prev => ({ ...prev, selected: name, loading: true }))

        clearTimeout(timeout)
        timeout = setTimeout(() => findInGoogle(name), 900)
    }

    const findInGoogle = name => {
        if (!services.autocomplete) return

        services.autocomplete.getQueryPredictions({ input: name }, (predictions, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                setState(prev => ({ ...prev, data: [], loading: false }))
                return
            }

            const mapped = predictions.map(p => ({ ...p, display_name: p.description }))
            setState(prev => ({ ...prev, data: mapped, loading: false }))
        })
    }

    const handleSelectGooglePlace = place => {
        if (!services.geocoder) return

        setState(prev => ({ ...prev, selected: place.display_name, data: [] }))

        resolveLocation({ placeId: place.place_id }, {
            place_id: place.place_id,
            label: 'pin placed from the search results',
        })
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`${state.overlay ? 'visible' : ''} overlay`}>
            <div className={`${state.modal ? 'show' : ''} popup-primary`}>

                <div className='head-new'>
                    <img src={popupDefaultHeadImage} alt="icon" />
                    <div className='texts'>
                        <h2>{trans('headings.address')}</h2>
                        <p>{trans('subheadings.address')}</p>
                    </div>
                    <span className='close' onClick={handleClose}></span>
                </div>

                <div className="alert warning">
                    {trans('messages.addressInfo')}
                </div>

                <div id="map-wrapper">
                    <div className="left">
                        <input
                            ref={inputRef}
                            type="search"
                            value={state.selected || ''}
                            onChange={handleSearch}
                            style={{ width: '100%' }}
                        />
                        {state.loading
                            ? <img className="loader" src={loader} />
                            : <ul>
                                {state.data.map(e =>
                                    <li
                                        key={'e-' + e.place_id}
                                        onClick={() => handleSelectGooglePlace(e)}
                                        className={e.place_id === state.place_id ? 'active' : ''}
                                    >
                                        <span>{e.display_name}</span>
                                    </li>
                                )}
                            </ul>
                        }
                    </div>

                    <div className="right">
                        <div className="map">
                            <Map
                                defaultCenter={CENTER}
                                defaultZoom={5}
                                mapId="DEMO_MAP_ID"
                                style={{ width: '100%', height: '100%' }}
                                disableDefaultUI={true}
                                keyboardShortcuts={false}
                            >
                                <MapOverlays
                                    state={state}
                                    handleMapClick={handleMapClick}
                                    inputRef={inputRef}
                                />
                            </Map>
                        </div>
                    </div>
                </div>

                <div className="footer align-right">
                    <button onClick={handleClose}>
                        {trans('buttons.cancel')}
                    </button>
                    <button className="button-primary" onClick={handleSave}>
                        {trans('buttons.save')}
                    </button>
                </div>

            </div>
        </div>
    )
}

// ─── Map overlays (must live inside <Map> context) ────────────────────────────
function MapOverlays({ state, handleMapClick, inputRef }) {
    const map = useMap()
    const [infoWindowOpen, setInfoWindowOpen] = useState(false)

    // Map click → place marker
    useEffect(() => {
        if (!map) return
        const listener = map.addListener('click', e => {
            handleMapClick(e.latLng.lat(), e.latLng.lng())
        })
        return () => window.google.maps.event.removeListener(listener)
    }, [map, handleMapClick])

    // When a location is set, zoom into it and auto-open the InfoWindow
    useEffect(() => {
        if (!map || !state.lat || !state.lon) return
        const lat = parseFloat(state.lat)
        const lng = parseFloat(state.lon)
        if (!isFinite(lat) || !isFinite(lng)) return
        map.setCenter({ lat, lng })
        map.setZoom(20)
        // setInfoWindowOpen(true)
    }, [map, state.lat, state.lon])

    // On modal open: trigger resize and focus the search input
    useEffect(() => {
        if (!map || !state.overlay || !state.modal) return
        window.google.maps.event.trigger(map, 'resize')
        if (inputRef?.current) inputRef.current.focus()
    }, [map, state.overlay, state.modal])

    if (!state.lat || !state.lon) return null

    const lat = parseFloat(state.lat)
    const lng = parseFloat(state.lon)
    if (!isFinite(lat) || !isFinite(lng)) return null

    const pos = { lat, lng }

    return (
        <>
            <AdvancedMarker
                position={pos}
                // onClick={() => setInfoWindowOpen(true)}
            >
                <img src={markerImg} width={25} height={41} alt="location" />
            </AdvancedMarker>

            {/* {infoWindowOpen && (
                <InfoWindow
                    position={pos}
                    onCloseClick={() => setInfoWindowOpen(false)}
                >
                    <div>{trans('random.reservationLocation')}</div>
                </InfoWindow>
            )} */}
        </>
    )
}

export default React.memo(forwardRef(Address), (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next)
})
